import React from 'react'

import {
  Box,
  Button,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  StepperButton,
  Text,
} from '@gousto-internal/citrus-react'
import { AgeVerificationCheckBox } from 'Product/AgeVerification'

import { ProductButtonTooltip } from './ProductButtonTooltip'
import { useProductButtonTooltip } from './useProductButtonTooltip'

type LimitReached =
  | {
      type: string
      value: string | number
    }
  | boolean

type ProductButtonsProps = {
  ageVerificationPending?: boolean
  canEditOrder?: boolean
  countLabel?: (qty: number) => React.ReactNode
  inProgress?: boolean
  isAgeVerificationRequired?: boolean
  isAvailable?: boolean
  limitReached?: LimitReached
  onAdd?: (productId: string) => void
  onRemove?: (productId: string) => void
  onVerifyAge?: (verified: boolean, showPopUp: boolean) => Promise<void>
  outOfStock?: boolean
  productId: string
  qty?: number
  showPopUp?: boolean
}

const STEPPER_INCREMENT_LABEL = 'Add item'

const getLimitReachedMessage = (limitReached: LimitReached = false) => {
  if (!limitReached || typeof limitReached === 'boolean') {
    return ''
  }

  switch (limitReached.type) {
    case 'box':
      return "Sorry, we can't fit anymore items in your box"
    case 'item':
      return "Sorry, we can't fit anymore of this item in your box"
    case 'category':
      return `Sorry, we can't fit anymore "${limitReached.value}" items in your box`
    default:
      return ''
  }
}

const ProductButtons = ({
  ageVerificationPending = false,
  canEditOrder = true,
  countLabel = (count) => count,
  inProgress = false,
  isAgeVerificationRequired = false,
  isAvailable = false,
  limitReached = false,
  onAdd = () => {},
  onRemove = () => {},
  onVerifyAge = async () => {},
  outOfStock = false,
  productId,
  qty = 0,
  showPopUp = false,
}: ProductButtonsProps) => {
  const [ageVerifyShowError, setAgeVerifyShowError] = React.useState(false)
  const [ageVerifyTooltipVisible, setAgeVerifyTooltipVisible] = React.useState(false)
  const isUnavailable = outOfStock || !isAvailable

  const tooltipMessage = React.useMemo(() => {
    if (canEditOrder === false) {
      return 'Sorry, you can no longer change your order'
    }

    if (outOfStock) {
      return "Sorry, we don't have any more in stock"
    }

    if (limitReached) {
      return getLimitReachedMessage(limitReached)
    }

    return ''
  }, [canEditOrder, limitReached, outOfStock])
  const {
    handleVisibilityChange: handleTooltipVisibilityChange,
    targetRef: tooltipTargetRef,
    visible: tooltipVisible,
  } = useProductButtonTooltip({
    isUnavailable,
    qty,
    stepperIncrementLabel: STEPPER_INCREMENT_LABEL,
    tooltipMessage,
  })

  const handleAdd = React.useCallback(() => {
    if (isAgeVerificationRequired && !showPopUp) {
      setAgeVerifyTooltipVisible(true)

      return
    }

    if (!isUnavailable) {
      onAdd(productId)
    }
  }, [isAgeVerificationRequired, isUnavailable, onAdd, productId, showPopUp])

  const handleRemove = React.useCallback(() => {
    onRemove(productId)
  }, [onRemove, productId])

  const handleAgeVerify = React.useCallback(
    async (verified: boolean) => {
      setAgeVerifyTooltipVisible(!verified)

      try {
        await onVerifyAge(verified, true)
      } catch (err) {
        setAgeVerifyShowError(true)
        setTimeout(() => {
          setAgeVerifyShowError(false)
        }, 5000)
      }
    },
    [onVerifyAge],
  )

  const isPending = !inProgress && ageVerificationPending

  const control =
    qty > 0 ? (
      <StepperButton
        count={qty}
        decrement={handleRemove}
        disableDecrement={isPending || outOfStock}
        disableIncrement={isPending || isUnavailable}
        fullWidth
        increment={handleAdd}
        incrementLabel={STEPPER_INCREMENT_LABEL}
      >
        <Text color={Color.ColdGrey_800} fontFamily={FontFamily.Bold} size={3}>
          {countLabel(qty)}
        </Text>
      </StepperButton>
    ) : (
      <Button
        aria-label={`${outOfStock ? 'Sold out' : 'Add'} product`}
        disabled={isPending || isUnavailable}
        onClick={handleAdd}
        width="100%"
      >
        {outOfStock ? 'Sold out' : 'Add'}
      </Button>
    )

  return (
    <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2} width="100%">
      {isAgeVerificationRequired && !showPopUp && (
        <AgeVerificationCheckBox
          disabled={inProgress || isUnavailable}
          onCheckBoxChange={handleAgeVerify}
          _onTooltipVisibleChange={setAgeVerifyTooltipVisible}
          showError={ageVerifyShowError}
          _tooltipVisible={ageVerifyTooltipVisible}
        />
      )}
      <ProductButtonTooltip message={tooltipMessage} visible={tooltipVisible}>
        {/* This wrapper is only a hover/touch target for the tooltip around the real button control. */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          data-testid="product-buttons-tooltip-target"
          ref={tooltipTargetRef}
          onBlur={(event) =>
            handleTooltipVisibilityChange(false, event.target, event.relatedTarget)
          }
          onFocus={(event) => handleTooltipVisibilityChange(true, event.target)}
          onMouseOut={(event) =>
            handleTooltipVisibilityChange(false, event.target, event.relatedTarget)
          }
          onMouseOver={(event) => handleTooltipVisibilityChange(true, event.target)}
          onPointerDownCapture={(event) => handleTooltipVisibilityChange(true, event.target)}
          onTouchStartCapture={(event) => handleTooltipVisibilityChange(true, event.target)}
        >
          {control}
        </div>
      </ProductButtonTooltip>
    </Box>
  )
}

export { ProductButtons }
